import type { GameStatCell } from '../build-game-card-data.js';
import { GameStat } from './GameStat.js';

/**
 * The primary-stat region of a game card's inset parchment panel (APP_SPEC
 * §13.3). Not a row of bordered boxes and not a spreadsheet: the cells sit on
 * one parchment surface, divided only by hairlines a shade darker than the
 * parchment itself.
 *
 * Column count follows how many metrics the game declares, and each layout is
 * sized so the region is *compact* — a six-metric game reads as 3×2 in around
 * 150px, a two-metric game as a confident full-width pair.
 *
 * The region is sized by its own content and nothing else. It is deliberately
 * *not* a flexible child that soaks up height from a taller sibling card: a
 * game declaring two metrics has one row to spend surplus on, so the surplus
 * became a tall empty cell with a stat centred in it rather than a roomier
 * layout. `auto-rows-fr` still equalises this game's own rows to each other,
 * which is what keeps a 3x2 grid on one internal baseline.
 *
 * The hairlines come from every cell carrying a top/left border while the grid
 * is pulled 1px up and left inside a clipping wrapper, so the outer edges fall
 * outside the visible box. That keeps the dividers correct at any column count
 * and across breakpoints without nth-child rules per layout.
 */
export interface GameStatGridProps {
  cells: GameStatCell[];
}

/**
 * The template always has exactly as many equal (`1fr`) columns as the row has
 * cells — never a wider template padded out with empty cells — so a
 * two-metric game divides the panel into two equal halves and a three-metric
 * row into equal thirds. Because the dividers are drawn by the cells
 * themselves (see below), they land on the real column edges at every count.
 *
 * One column on a phone whatever the metric count: the card there is barely
 * 250px wide, and two columns left a cell too narrow for its own label and a
 * long unbreakable value, which then spilled out under the card's
 * `overflow-hidden`.
 */
function columnsFor(count: number): string {
  if (count <= 2) return 'grid-cols-1 tablet:grid-cols-2';
  if (count === 4) return 'grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4';
  return 'grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3';
}

export function GameStatGrid({ cells }: GameStatGridProps) {
  if (cells.length === 0) {
    return (
      <p className="px-5 py-6 text-center text-sm text-textMuted">No statistics recorded yet.</p>
    );
  }

  // Dense layouts keep their horizontal padding tight: the panel is inset from
  // the card's gold rail on both sides, and at three columns a wider gutter is
  // what pushes a label like "Best word score" onto a second line.
  const cellPadding =
    cells.length <= 2 ? 'px-3 py-6 tablet:px-4' : 'px-2 py-4 tablet:px-2.5 tablet:py-[1.15rem]';

  return (
    <div className="overflow-hidden">
      <div className={`-ml-px -mt-px grid auto-rows-fr ${columnsFor(cells.length)}`}>
        {cells.map((cell) => (
          <div
            key={cell.key}
            // `min-w-0`: a grid item defaults to `min-width: auto`, so a long
            // unbreakable value set the column's floor and the narrow layouts
            // pushed their labels out under `overflow-hidden` instead of
            // wrapping them.
            // `items-center justify-center` on the cell, with `GameStat`
            // shrink-wrapping itself: the cell is the centring box and the
            // stat is the group being centred, on both axes. A stat that
            // stretched to the cell and left-aligned inside would satisfy
            // "the grid columns are equal" and still render every cluster
            // against the left wall of its column.
            className={`flex min-w-0 items-center justify-center border-l border-t border-borderStrong/12 ${cellPadding}`}
          >
            <GameStat
              iconSrc={cell.iconSrc}
              label={cell.label}
              value={cell.value}
              density={cells.length <= 2 ? 'roomy' : 'dense'}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
